/**
 * Calendar arithmetic for the console's month grid.
 *
 * Every day here is a `YYYY-MM-DD` string in UTC, because that is what the
 * rest of the product means by a day: `listByDate` compares on the ISO
 * prefix, `formatDay` renders in UTC, and a grid that grouped appointments by
 * the viewer's local day would put a 23:30 booking on the wrong square for
 * half the world. The dates below are built at midday UTC so that no amount
 * of daylight-saving arithmetic can tip one into its neighbour.
 */

const DAY_MS = 86_400_000

function utc(day: string): Date {
  return new Date(`${day}T12:00:00.000Z`)
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/** The `YYYY-MM` a day falls in. */
export function monthOf(day: string): string {
  return day.slice(0, 7)
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const zeroBased = m - 1 + delta
  const year = y + Math.floor(zeroBased / 12)
  const index = ((zeroBased % 12) + 12) % 12
  return `${year}-${`${index + 1}`.padStart(2, '0')}`
}

/** The first and last day of the month, both of them days the caller wants. */
export function monthRange(month: string): { readonly from: string; readonly to: string } {
  const from = `${month}-01`
  const [y, m] = month.split('-').map(Number)
  // Day 0 of the next month is the last day of this one — the only way to say
  // it that never has to know which months have 31 days, or which Februaries.
  const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0, 12))
  return { from, to: toIso(last) }
}

export type GridDay = {
  readonly iso: string
  readonly dayOfMonth: number
  readonly inMonth: boolean
}

/**
 * Six weeks of squares, always — a grid that changes height as you page
 * through the year makes everything under it jump.
 *
 * Weeks start on Sunday, which is how the boutiques this ships to read a
 * calendar. The weekday LABELS come from the viewer's locale (see
 * `weekdayLabels`); only the column order is fixed here.
 */
export function buildMonthGrid(month: string): GridDay[] {
  const first = utc(`${month}-01`)
  const start = new Date(first.getTime() - first.getUTCDay() * DAY_MS)
  const days: GridDay[] = []
  for (let i = 0; i < 42; i++) {
    const date = new Date(start.getTime() + i * DAY_MS)
    const iso = toIso(date)
    days.push({ iso, dayOfMonth: date.getUTCDate(), inMonth: iso.startsWith(month) })
  }
  return days
}

/** Sunday-first weekday names, in the viewer's language. */
export function weekdayLabels(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
  // 2024-01-07 was a Sunday. Any Sunday would do; a constant one keeps this
  // from depending on when it runs.
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(Date.UTC(2024, 0, 7 + i, 12))))
}

export function monthLabel(month: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    utc(`${month}-01`),
  )
}

/** Shift a `YYYY-MM-DD` by whole days, staying in UTC. */
export function shiftDay(day: string, delta: number): string {
  return toIso(new Date(utc(day).getTime() + delta * DAY_MS))
}
